import {FC} from "react";
import {EventApprover} from "../../../../types/domain.ts";
import {Button, Flex, theme, Typography} from "antd";
import {
    CheckCircleFilled,
    CheckCircleOutlined,
    ClockCircleFilled,
    CloseCircleFilled,
    RightCircleFilled
} from "@ant-design/icons";
import {useEventComments} from "../../../../queries/useEventComments.ts";
import dayjs from "dayjs";
import {isFirManager, isRegionManager} from "../../../../utils/userTypeGuards.ts";
import {useOwnProfile} from "../../../../stores/OwnProfileStore.ts";

type Props = {
    eventId: string;
    approver: EventApprover;
    approvers: EventApprover[];
    approveLoading: boolean;
    onApprove: VoidFunction;
    approvable: boolean;
}

const getApprovingText = (approvers: EventApprover[], approver: EventApprover) => {
    if(approver.reason !== 'staff') return 'На согласовании';
    if(approvers.every(({ reason, status }) => reason === 'staff' || status === 'approved')) return 'На утверждении';
    return 'Ожидается к утверждению';
}

export const ApproverStatus: FC<Props> = ({ eventId, approver, approvers, approveLoading, onApprove, approvable }) => {
    const { token } = theme.useToken();
    const { profile } = useOwnProfile();

    const { data: comments } = useEventComments(eventId);

    if(approver.status === 'rejected') {
        const comment = comments?.find(({ type, created_by }) => type === 'return' && created_by.id === approver.user?.id);

        return (
            <Flex gap={16}>
                <Flex vertical>
                    <Typography.Text>Не согласовано</Typography.Text>
                    {comment && (
                        <Typography.Text type="secondary">{dayjs.utc(comment.created_at).format('DD.MM.YYYY HH:mm[Z]')}</Typography.Text>
                    )}
                </Flex>
                <CloseCircleFilled
                    style={{ color: token.colorError, fontSize: '16px' }}
                />
            </Flex>
        )
    }

    if(((profile?.role === 'staff' && approver.reason === 'staff') || approver.user?.id === profile?.id) && approvable) {
        return (
            <Button
                size="small"
                color="green"
                variant="outlined"
                icon={<CheckCircleOutlined />}
                loading={approveLoading}
                onClick={onApprove}
            >
                Согласовать
            </Button>
        )
    }

    if(approver.status === 'approved') {
        const comment = comments?.find(({ type, created_by }) => type === 'approve' && created_by.id === approver.user?.id);

        return (
            <Flex gap={16}>
                <Flex vertical>
                    <Typography.Text>Согласовано</Typography.Text>
                    {comment && (
                        <Typography.Text type="secondary">{dayjs.utc(comment.created_at).format('DD.MM.YYYY HH:mm[Z]')}</Typography.Text>
                    )}
                </Flex>
                <CheckCircleFilled
                    style={{ color: token.colorSuccess, fontSize: '16px' }}
                />
            </Flex>
        )
    }

    if(
        approver.user && isRegionManager(approver.user) &&
        approvers.find(({ reason, user }) =>
            user &&
            isFirManager(user) &&
            reason === 'forward' &&
            user.firs.some(({ region }) =>
                approver.user &&
                isRegionManager(approver.user) &&
                region.id === approver.user?.region.id
            )
        )
    ) {
        const comment = comments?.find(({ type, created_by }) => type === 'forward' && created_by.id === approver.user?.id);

        return (
            <Flex gap={16}>
                <Flex vertical>
                    <Typography.Text>Перенаправлено</Typography.Text>
                    {comment && (
                        <Typography.Text type="secondary">{dayjs.utc(comment.created_at).format('DD.MM.YYYY HH:mm[Z]')}</Typography.Text>
                    )}
                </Flex>
                <RightCircleFilled
                    style={{ color: token.colorTextTertiary, fontSize: '16px' }}
                />
            </Flex>
        )
    }

    return (
        <Flex gap={16}>
            <Typography.Text>{getApprovingText(approvers, approver)}</Typography.Text>
            <ClockCircleFilled
                style={{ color: token.colorTextTertiary, fontSize: '16px' }}
            />
        </Flex>
    )
}